import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { reportService } from "../../services/reportService";
import Layout from "../../components/Layout/Layout";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  AG_GRID_LOCALE_TR,
  AG_GRID_LOCALE_EN,
} from "@ag-grid-community/locale";
import { themeQuartz } from "ag-grid-community";
import { colorSchemeDarkBlue } from "ag-grid-community";
import i18n from "../../i18n/i18n";

const SaleSpotReport = () => {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [endDate] = useState(dayjs().format("DD-MM-YYYY"));
  const [, setGridApi] = useState(null);
  const [startDate] = useState(
    dayjs().subtract(2, "year").format("DD-MM-YYYY")
  );
  const { t } = useTranslation();

  const onGridReady = (params) => {
    setGridApi(params.api);
    params.api.sizeColumnsToFit();
  };

  const gridLocale = useMemo(() => {
    return i18n.language === "tr" ? AG_GRID_LOCALE_TR : AG_GRID_LOCALE_EN;
  }, []);

  const columnDefs = [
    {
      field: "label",
      headerName: t("salespotreport.date_range"),
      sortable: true,
      filter: true,
      cellRenderer: "agGroupCellRenderer",
    },
    {
      field: "transaction_count",
      headerName: t("salespotreport.transaction_count"),
      sortable: true,
      filter: true,
    },
    {
      field: "product_count",
      headerName: t("salespotreport.product_count"),
      sortable: true,
      filter: true,
    },
    {
      field: "total_price",
      headerName: t("salespotreport.total_price"),
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        return (
          params.value?.toLocaleString("tr-TR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) + " ₺"
        );
      },
    },
    {
      field: "total_customer_count",
      headerName: t("salespotreport.total_customer_count"),
      sortable: true,
      filter: true,
    },
  ];

  const detailColumnDefs = [
    {
      field: "label",
      headerName: t("salespotreport.spot_name"),
      sortable: true,
      filter: true,
    },
    {
      field: "transaction_count",
      headerName: t("salespotreport.transaction_count"),
      sortable: true,
      filter: true,
    },
    {
      field: "product_count",
      headerName: t("salespotreport.product_count"),
      sortable: true,
      filter: true,
    },
    {
      field: "total_price",
      headerName: t("salespotreport.total_price"),
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        return (
          params.value?.toLocaleString("tr-TR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) + " ₺"
        );
      },
    },
    {
      field: "customer_count",
      headerName: t("salespotreport.customer_count"),
      sortable: true,
      filter: true,
    },
  ];

  const defaultColDef = {
    flex: 1,
    minWidth: 150,
    resizable: true,
    sortable: true,
    filter: true,
  };

  const getDetailRowData = (params) => {
    params.successCallback(params.data.spot_based);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await reportService().productSaleReportSaleSpot(
        startDate,
        endDate
      );
      setRowData(response.data);
    } catch (error) {
      console.error("Veri getirme hatası:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">{t("salespotreport.title")}</h1>

        {loading ? (
          <div className="flex items-center justify-center h-full">
            <span>{t("salespotreport.loading")}</span>
          </div>
        ) : (
          <div
            className="ag-theme-alpine dark:ag-theme-alpine-dark"
            style={{ height: "600px", width: "100%" }}
          >
            <AgGridReact
              theme={themeQuartz.withPart(colorSchemeDarkBlue)}
              localeText={gridLocale}
              columnDefs={columnDefs}
              rowData={rowData}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={true}
              paginationPageSize={20}
              rowSelection="multiple"
              sideBar={true}
              key={i18n.language}
              masterDetail={true}
              detailCellRendererParams={{
                detailGridOptions: {
                  columnDefs: detailColumnDefs,
                  defaultColDef: defaultColDef,
                },
                getDetailRowData: getDetailRowData,
              }}
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SaleSpotReport;
